import { ChangeDetectionStrategy, Component, computed, Directive, input } from '@angular/core';
import { Icon, type IconInput } from '../icon/icon';

/** How much room the frame takes: a whole screen, or a gap inside one. */
export type EmptyStateVariant = 'page' | 'inline';

/** The element the title renders as. See the note on `titleAs`. */
export type EmptyStateTitleAs = 'h1' | 'h2' | 'p';

/**
 * Empty State — the centred "nothing to show you here" frame: an optional icon
 * chip above a title, a line of body, and whatever action the screen offers.
 *
 * ```html
 * <ui-empty-state icon="lock" titleAs="h1" heading="You can't open this page">
 *   <p uiEmptyStateBody>Your role here doesn't include the permission it needs.</p>
 *   <a ui-link-button routerLink="/dashboard">Back to the dashboard</a>
 * </ui-empty-state>
 * ```
 *
 * **The title's element is an input, not an assumption.** The call sites need
 * three different ones and each is right where it is: a screen that *replaces*
 * the page carries the `h1`, a frame under a page that already has one carries an
 * `h2`, and a frame inside a table cell carries neither — a heading there would
 * announce structure that isn't there. The type role follows the element, because
 * in every case so far it has.
 *
 * Not from Figma. The design library has no empty state, so this is the audit
 * log's frame promoted once four more screens had copied it.
 *
 * Title and body take their type from the foundations roles (`.heading`,
 * `.body-md`, …) rather than being restyled here. `.heading` is a `fluid-type()`
 * clamp assembled from SCSS primitives components are documented never to consume
 * directly, so re-deriving it would be a copy that drifts on the next sync —
 * whereas every app consuming this library already loads `foundations`.
 */
@Component({
  selector: 'ui-empty-state',
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './empty-state.scss',
  host: { '[class]': 'hostClasses()' },
  template: `
    @if (icon(); as name) {
      <span class="ui-empty-state__icon" aria-hidden="true">
        <ui-icon [name]="name" [strokeWidth]="1.5" />
      </span>
    }

    @switch (titleAs()) {
      @case ('h1') {
        <h1 class="ui-empty-state__title heading">{{ heading() }}</h1>
      }
      @case ('h2') {
        <h2 class="ui-empty-state__title subheading">{{ heading() }}</h2>
      }
      @default {
        <p class="ui-empty-state__title body-lg strong">{{ heading() }}</p>
      }
    }

    <ng-content />
  `,
})
export class EmptyState {
  readonly heading = input.required<string>();

  /**
   * The chip's glyph. Optional: the inline frames drop it, the way `ui-card`
   * drops its media box when nothing is projected into it.
   */
  readonly icon = input<IconInput>();

  readonly variant = input<EmptyStateVariant>('page');

  /**
   * The heading level, and with it the type role. Pass it explicitly — the
   * default is the safe one (an `h2` under a page's own `h1`) rather than the
   * most common one.
   */
  readonly titleAs = input<EmptyStateTitleAs>('h2');

  protected readonly hostClasses = computed(
    () => `ui-empty-state ui-empty-state--${this.variant()}`,
  );
}

/**
 * The line of body under the title.
 *
 * It carries its classes and its layout on the consumer's own element rather than
 * being styled from `empty-state.scss`: projected content keeps the *consumer's*
 * style encapsulation, so no rule scoped to this component could reach it. Same
 * reason as `uiCtaBannerBadge`.
 *
 * `uiEmptyStateBody="sm"` drops to `.body-sm`, for a second and quieter line
 * under the first — one screen has one.
 */
@Directive({
  selector: '[uiEmptyStateBody]',
  host: {
    class: 'ui-empty-state__body text-secondary',
    // The measure is a custom property set by the frame, so the body follows the
    // variant even though it lives outside this component's styles.
    style: 'margin:0; max-width:var(--ui-empty-state-measure)',
    '[class.body-md]': "size() === 'md'",
    '[class.body-sm]': "size() === 'sm'",
  },
})
export class EmptyStateBody {
  /**
   * Transformed rather than defaulted, because the common form — a bare
   * `uiEmptyStateBody` attribute — sets the input to the empty string, which
   * would otherwise leave the body with no type role at all.
   */
  readonly size = input<'md' | 'sm', '' | 'md' | 'sm'>('md', {
    alias: 'uiEmptyStateBody',
    transform: (value) => (value === 'sm' ? 'sm' : 'md'),
  });
}
